import { useMemo } from 'react';
import { ActionAssigneeKind, IapScope, INCIDENT } from '../ICSConstants';
import { useActionAssigneeDirectory } from './useActionAssigneeDirectory';
import { actionEditBody } from '../actionsTable/actionEditModel';
import {
  GetIncidentActionResponse,
  useCreateIncidentAction,
  useUpdateIncidentAction,
} from './incidentActionHooks';
import {
  GetOrgActionResponse,
  useCreateOrgAction,
  useOrgActions,
  useUpdateOrgAction,
} from './orgActionHooks';
import { useIncidentActions } from './incidentActionHooks';
import { dependencyOptions } from '../utils/actionDependencies';
import type { GeoLocation } from 'components/GeoLocationComponent/buildGeoLocationFromGeoJson';
import type { ActionTicketFieldValues } from '../actionTicket/actionTicketModel';
import type { ActionChecklistItem } from '../actionChecklistModel';

export type ActionFormValues = {
  name: string;
  description: string;
  poc?: string | null;
  briefed_poc?: boolean;
  status?: string | null;
  priority?: string | null;
  /**
   * `poc` stays the wire field for user assignment; these two carry team and
   * position assignment. The server reconciles the pair (see coms
   * actionTaskFields.ts), so the form only has to send what the user picked.
   */
  assignee_kind?: ActionAssigneeKind | null;
  assignee_id?: string | null;
  /**
   * Operational / Managerial / both. Incident scope only: it decides whether a
   * task prints on the ICS-233 or materialises into the ICS-215, and org
   * actions belong to neither form.
   */
  classification?: string | null;
  /**
   * A `UscgSymbol` key, or '' for the marker derived from status and priority.
   * Both scopes: org actions are drawn on the hub map too.
   */
  symbology?: string | null;
  start_date?: Date | string | null;
  target_date?: Date | string | null;
  actual_date?: Date | string | null;
  resources?: string[];
  geolocation?: GeoLocation | null;
  aor_id?: string | null;
  /**
   * Occupants named as responsible when the task is assigned to an ICS
   * position. Incident scope only; empty otherwise.
   */
  responsible_individual_ids?: string[];
  /**
   * Incident objective this task is in service of. Stored as
   * `ics_incident_actions.parent_id` → `objective_id`. Empty/null is none.
   */
  parent_id?: string | null;
  /** Predecessor `action_id`s: this task cannot start until they are Closed. */
  depends_on?: string[];
  checklist: ActionChecklistItem[];
} & ActionTicketFieldValues;

export type ActionEntity = GetIncidentActionResponse | GetOrgActionResponse;

const browserUtcOffset = () => -new Date().getTimezoneOffset();

/**
 * Abstracts the incident-vs-org differences (POC source, resources picker, and
 * which create/update/archive mutations fire) behind one uniform interface so
 * the Action drawer can stay scope-agnostic. Both sides' hooks are instantiated
 * unconditionally (rules of hooks); the unused side stays inert because its
 * queries are `enabled`-gated and its mutations simply aren't invoked.
 */
export const useScopedActionForm = (
  scope: IapScope,
  incidentIdOverride?: string | null,
  /** The action being edited, whose own descendants cannot become its blockers. */
  editingActionId?: string | null
) => {
  const isIncident = scope === INCIDENT;

  // Incident side
  const createIncident = useCreateIncidentAction(incidentIdOverride);
  const updateIncident = useUpdateIncidentAction(incidentIdOverride);

  // Org side
  const createOrg = useCreateOrgAction();
  const updateOrg = useUpdateOrgAction();

  const {
    users: pocOptions,
    teams: teamOptions,
    positions: positionOptions,
    roster = [],
  } = useActionAssigneeDirectory(scope, incidentIdOverride);

  /**
   * The tasks that could legally block this one. Both lists are already in the
   * cache behind the tables that opened this form, so this costs nothing beyond
   * the filtering; `useOrgActions` and `useIncidentActions` are both
   * scope-gated, so the unused side never fetches.
   */
  const incidentActionsQuery = useIncidentActions(scope, incidentIdOverride);
  const orgActionsQuery = useOrgActions(scope);
  const dependencyChoices = useMemo(() => {
    const actions = isIncident
      ? incidentActionsQuery.data
      : orgActionsQuery.data;
    return dependencyOptions(editingActionId, actions ?? []);
  }, [
    isIncident,
    incidentActionsQuery.data,
    orgActionsQuery.data,
    editingActionId,
  ]);

  const create = async (values: ActionFormValues) => {
    const parentId = values.parent_id || null;
    const payload = {
      ...values,
      description: values.description ?? '',
      aor_id: values.aor_id || null,
      parent_id: parentId,
      // Create reads camelCase; update reads snake_case. Send both.
      parentId,
    };
    if (isIncident) {
      return createIncident.mutateAsync({ ...payload, archived: false } as any);
    }
    return createOrg.mutateAsync({
      ...payload,
      utc_offset: browserUtcOffset(),
    } as any);
  };

  const update = async (entity: ActionEntity, values: ActionFormValues) => {
    const payload = {
      ...values,
      description: values.description ?? '',
      aor_id: values.aor_id || null,
      parent_id: values.parent_id || null,
    };
    if (isIncident) {
      const inc = entity as GetIncidentActionResponse;
      return updateIncident.mutateAsync({
        ...payload,
        id: inc.id,
        action_id: inc.action_id,
      } as any);
    }
    const org = entity as GetOrgActionResponse;
    return updateOrg.mutateAsync({
      ...payload,
      action_id: org.action_id,
      utc_offset: browserUtcOffset(),
      archived: false,
    } as any);
  };

  /**
   * Archiving is the same read-modify-write the inline cells do: send the row
   * back with one field changed, minus the join output the write path must not
   * see. Enumerating the fields by hand here used to silently drop
   * `parent_id`.
   */
  const archive = async (entity: ActionEntity, archived: boolean) => {
    const body = actionEditBody(entity as unknown as Record<string, unknown>, {
      archived,
    });
    if (isIncident) {
      return updateIncident.mutateAsync(body as any);
    }
    return updateOrg.mutateAsync({
      ...body,
      utc_offset: browserUtcOffset(),
    } as any);
  };

  const isSaving = isIncident
    ? createIncident.isPending || updateIncident.isPending
    : createOrg.isPending || updateOrg.isPending;

  return {
    pocOptions,
    positionOptions,
    teamOptions,
    roster,
    dependencyChoices,
    showClassification: isIncident,
    create,
    update,
    archive,
    isSaving,
  };
};
